import type { SnapshotDetail, SnapshotOverlap } from 'services/snapshotsService';
import { classifyOverlapPlaybooks } from './snapshotUtils';

export const MOCK_SNAPSHOT_ID = 'preview';

type MockAccount = {
  company: string;
  lifecycleStage: 'lead' | 'opportunity' | 'customer';
  accountOwner?: string;
};

function mockOverlap(
  id: string,
  domain: string,
  initiator: MockAccount,
  partner: MockAccount,
): SnapshotOverlap {
  return {
    id,
    domain,
    initiator,
    partner,
    playbooks: classifyOverlapPlaybooks(
      initiator.lifecycleStage,
      partner.lifecycleStage,
    ),
  };
}

const MOCK_ROWS: SnapshotOverlap[] = [
  mockOverlap(
    '1',
    'globex.com',
    { company: 'Globex Corporation', lifecycleStage: 'customer', accountOwner: 'Alex Rivera' },
    { company: 'Globex Corp', lifecycleStage: 'opportunity', accountOwner: 'Sam Chen' },
  ),
  mockOverlap(
    '2',
    'initech.com',
    { company: 'Initech', lifecycleStage: 'lead', accountOwner: 'Jordan Blake' },
    { company: 'Initech Ltd', lifecycleStage: 'customer', accountOwner: 'Priya Shah' },
  ),
  mockOverlap(
    '3',
    'umbrella-corp.io',
    { company: 'Umbrella Corp', lifecycleStage: 'customer', accountOwner: 'Chris Patel' },
    { company: 'Umbrella Holdings', lifecycleStage: 'customer', accountOwner: 'Taylor Kim' },
  ),
  mockOverlap(
    '4',
    'starkindustries.com',
    { company: 'Stark Industries', lifecycleStage: 'opportunity', accountOwner: 'Morgan Lee' },
    { company: 'Stark Industries', lifecycleStage: 'customer', accountOwner: 'Riley Brooks' },
  ),
  mockOverlap(
    '5',
    'wayneenterprises.com',
    { company: 'Wayne Enterprises', lifecycleStage: 'customer', accountOwner: 'Casey Nguyen' },
    { company: 'Wayne Enterprises', lifecycleStage: 'customer', accountOwner: 'Drew Alvarez' },
  ),
  mockOverlap(
    '6',
    'hooli.xyz',
    { company: 'Hooli', lifecycleStage: 'lead', accountOwner: 'Alex Rivera' },
    { company: 'Hooli Inc', lifecycleStage: 'opportunity', accountOwner: 'Sam Chen' },
  ),
  mockOverlap(
    '7',
    'piedpiper.com',
    { company: 'Pied Piper', lifecycleStage: 'opportunity', accountOwner: 'Jordan Blake' },
    { company: 'Pied Piper', lifecycleStage: 'customer', accountOwner: 'Priya Shah' },
  ),
  mockOverlap(
    '8',
    'soylent.io',
    { company: 'Soylent Corp', lifecycleStage: 'customer', accountOwner: 'Chris Patel' },
    { company: 'Soylent', lifecycleStage: 'lead', accountOwner: 'Taylor Kim' },
  ),
];

function playbookCounts(rows: SnapshotOverlap[]) {
  const counts = {
    upsells: 0,
    accelerate: 0,
    generate: 0,
    partnerships: 0,
    leadToLead: 0,
  };
  for (const row of rows) {
    for (const key of row.playbooks) {
      if (key in counts) counts[key as keyof typeof counts] += 1;
    }
  }
  return counts;
}

export const MOCK_SNAPSHOT_DETAIL: SnapshotDetail = {
  id: MOCK_SNAPSHOT_ID,
  status: 'ready',
  initiatorCompanyName: 'Acme Analytics',
  partnerCompanyName: 'Northwind Systems',
  partnerCompanyDomain: 'northwind.io',
  partnerEmail: 'partnerships@northwind.io',
  readyAt: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000).toISOString(),
  expiresAt: new Date(Date.now() + 12 * 24 * 60 * 60 * 1000).toISOString(),
  counts: {
    initiatorCount: 48,
    partnerCount: 36,
    overlapping: MOCK_ROWS.length,
    overlapRate: 17,
    overlapRateMin: 17,
    overlapRateMax: 22,
  },
  playbooks: playbookCounts(MOCK_ROWS),
  isInitiator: true,
};

export function getMockSnapshotOverlaps(playbookKey: string): SnapshotOverlap[] {
  return MOCK_ROWS.filter((row) => row.playbooks.includes(playbookKey));
}
